/**
 * MediaItemsGallery - Generic, reusable media/text items gallery
 * - Automatically switches orientation based on available width
 * - Horizontal row of mini-cards for wide containers
 * - Vertical stacked mini-cards when container is narrow (<= 150px)
 * - Supports images, videos, documents, audio, and text
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Stack,
} from "@mui/material";
import {
  Image as ImageIcon,
  VideoFile as VideoIcon,
  Description as DocumentIcon,
  TextSnippet as TextIcon,
  Visibility as PreviewIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import { logDebug, logWarn } from "../../shared/utils/logger.js";

const DEFAULT_MINI_CARD_WIDTH = 120;
const DEFAULT_MINI_CARD_HEIGHT = 140;

export default function MediaItemsGallery({
  items = [],
  entityId, // leadId or quoteId
  entityType = "quotes", // "quotes" | "leads"
  maxItems = 10,
  miniCardWidth = DEFAULT_MINI_CARD_WIDTH,
  miniCardHeight = DEFAULT_MINI_CARD_HEIGHT,
}) {
  logDebug("MediaItemsGallery: initialized", {
    entityId,
    entityType,
    itemsCount: items.length,
    items,
  });
  const containerRef = useRef(null);
  const [isVertical, setIsVertical] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [loadingImages, setLoadingImages] = useState(new Set());

  // Determine orientation by container width
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const compute = () => {
      const width = el.clientWidth || 0;
      setIsVertical(width <= 150);
    };

    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const displayItems = useMemo(
    () => items.slice(0, maxItems),
    [items, maxItems]
  );

  const getMediaIcon = (type) => {
    const icons = {
      image: <ImageIcon color="primary" />,
      video: <VideoIcon color="secondary" />,
      document: <DocumentIcon color="info" />,
      audio: <DocumentIcon color="warning" />,
      spreadsheet: <DocumentIcon color="success" />,
      csv: <DocumentIcon color="success" />,
      text: <TextIcon color="action" />,
    };
    return icons[type] || <TextIcon color="action" />;
  };

  const getMediaEmoji = (type) => {
    const emojis = {
      image: "🖼️",
      video: "🎥",
      document: "📄",
      audio: "🎵",
      spreadsheet: "📊",
      csv: "📈",
      text: "📝",
    };
    return emojis[type] || "📦";
  };

  const getMediaUrl = (item) => {
    if (item.type === "text") return null;

    logDebug("MediaItemsGallery: computing media URL", {
      type: item.type,
      id: item.id,
      filename: item.filename,
      previewUrl: item.previewUrl,
      downloadUrl: item.downloadUrl,
    });

    // Prefer absolute Firebase URLs only (avoid relative /api when broken)
    if (item.previewUrl && /^(https?:)?\/\//.test(item.previewUrl)) {
      logDebug("MediaItemsGallery: using absolute preview URL", {
        type: item.type,
        url: item.previewUrl,
      });
      return item.previewUrl;
    }
    if (item.downloadUrl && /^(https?:)?\/\//.test(item.downloadUrl)) {
      logDebug("MediaItemsGallery: using absolute download URL", {
        type: item.type,
        url: item.downloadUrl,
      });
      return item.downloadUrl;
    }

    // If previewUrl exists but is broken (contains undefined), ignore it
    const hasBrokenPreview =
      typeof item.previewUrl === "string" &&
      item.previewUrl.includes("undefined");

    if (item.previewUrl && !hasBrokenPreview) {
      logDebug("MediaItemsGallery: using relative preview URL", {
        type: item.type,
        url: item.previewUrl,
      });
      return item.previewUrl;
    }

    // Fallback to nginx-served path (no /api prefix needed)
    const last = item.filename || item.id;
    const url = `/media/${entityType}/${entityId}/${item.type}/${last}`;
    logWarn("MediaItemsGallery: using fallback media URL", {
      type: item.type,
      url,
      reason: hasBrokenPreview
        ? "previewUrl contained undefined"
        : "no preview/download URL found",
    });
    return url;
  };

  const getDownloadUrl = (item) => {
    // Prefer absolute Firebase download URL
    if (item.downloadUrl && /^(https?:)?\/\//.test(item.downloadUrl)) {
      return item.downloadUrl;
    }
    // If relative downloadUrl exists and isn't broken, use it
    const hasBrokenDownload =
      typeof item.downloadUrl === "string" &&
      item.downloadUrl.includes("undefined");
    if (item.downloadUrl && !hasBrokenDownload) {
      return item.downloadUrl;
    }
    const last = item.filename || item.id;
    return `/media/${entityType}/${entityId}/${item.type}/${last}`;
  };

  const handlePreview = (item) => {
    if (item.type === "text") return;
    const url = getMediaUrl(item);
    logDebug("MediaItemsGallery: preview item", { url, item });
    setPreviewItem(item);
    setPreviewOpen(true);
  };

  const handleDownload = (item) => {
    if (item.type === "text") return;

    const url = getDownloadUrl(item);
    logDebug("MediaItemsGallery: download item", {
      type: item.type,
      url,
    });

    const link = document.createElement("a");
    link.href = url;
    link.download = item.filename || `${item.id}.${item.type}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!items || items.length === 0) {
    return (
      <Box>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontStyle: "italic" }}
        >
          No items
        </Typography>
      </Box>
    );
  }

  return (
    <Box ref={containerRef}>
      <Stack direction="row" spacing={1} alignItems="center" mb={1}>
        <Typography
          variant="subtitle2"
          color="primary"
          sx={{ fontWeight: 600 }}
        >
          Items ({items.length})
        </Typography>
      </Stack>

      {/* Adaptive layout */}
      {isVertical ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {displayItems.map((item, index) => (
            <MiniCard
              key={item.id || index}
              item={item}
              width={miniCardWidth}
              height={miniCardHeight}
              loadingImages={loadingImages}
              setLoadingImages={setLoadingImages}
              getMediaUrl={getMediaUrl}
              getMediaIcon={getMediaIcon}
              getMediaEmoji={getMediaEmoji}
              onPreview={handlePreview}
            />
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            gap: 1,
            overflowX: "auto",
            pb: 1,
            "&::-webkit-scrollbar": { height: 6 },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "grey.100",
              borderRadius: 3,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "grey.400",
              borderRadius: 3,
              "&:hover": { backgroundColor: "grey.600" },
            },
          }}
        >
          {displayItems.map((item, index) => (
            <MiniCard
              key={item.id || index}
              item={item}
              width={miniCardWidth}
              height={miniCardHeight}
              loadingImages={loadingImages}
              setLoadingImages={setLoadingImages}
              getMediaUrl={getMediaUrl}
              getMediaIcon={getMediaIcon}
              getMediaEmoji={getMediaEmoji}
              onPreview={handlePreview}
            />
          ))}
        </Box>
      )}

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {previewItem?.description || previewItem?.filename || "Media Preview"}
        </DialogTitle>
        <DialogContent>
          {previewItem && (
            <Box sx={{ textAlign: "center" }}>
              {previewItem.type === "image" && (
                <img
                  src={getMediaUrl(previewItem)}
                  alt={previewItem.description || "Item image"}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "70vh",
                    objectFit: "contain",
                  }}
                />
              )}
              {previewItem.type === "video" && (
                <video
                  src={getMediaUrl(previewItem)}
                  controls
                  style={{ maxWidth: "100%", maxHeight: "70vh" }}
                />
              )}
              {previewItem.type === "audio" && (
                <audio
                  src={getMediaUrl(previewItem)}
                  controls
                  style={{ width: "100%", maxWidth: 400 }}
                />
              )}
              {previewItem.type === "document" && (
                <Box sx={{ p: 4 }}>
                  <DocumentIcon
                    sx={{ fontSize: 80, color: "text.secondary", mb: 2 }}
                  />
                  <Typography variant="h6" gutterBottom>
                    {previewItem.filename || "Document"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    This document cannot be previewed in the browser.
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
          {previewItem && previewItem.type !== "text" && (
            <Button
              startIcon={<DownloadIcon />}
              onClick={() => {
                handleDownload(previewItem);
                setPreviewOpen(false);
              }}
              variant="contained"
              color="primary"
            >
              Download
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function MiniCard({
  item,
  width,
  height,
  loadingImages,
  setLoadingImages,
  getMediaUrl,
  getMediaIcon,
  getMediaEmoji,
  onPreview,
}) {
  return (
    <Card
      variant="outlined"
      sx={{
        width,
        height,
        display: "flex",
        flexDirection: "column",
        transition: "all 0.2s ease-in-out",
        cursor: item.type !== "text" ? "pointer" : "default",
        "&:hover": { boxShadow: 2, transform: "translateY(-2px)" },
        flexShrink: 0,
      }}
      onClick={() => item.type !== "text" && onPreview(item)}
    >
      <CardContent sx={{ flexGrow: 1, p: 1, "&:last-child": { pb: 1 } }}>
        <Stack direction="row" spacing={0.5} alignItems="center" mb={0.5}>
          {getMediaIcon(item.type)}
          {item.quantity && item.quantity > 1 && (
            <Chip
              label={`×${item.quantity}`}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontSize: "0.7rem", height: 20 }}
            />
          )}
        </Stack>

        {/* Visual area */}
        {item.type !== "text" ? (
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: 80,
              mb: 0.5,
              borderRadius: 1,
              overflow: "hidden",
              backgroundColor: "grey.100",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {loadingImages.has(item.id) && <CircularProgress size={20} />}

            {item.type === "image" && !loadingImages.has(item.id) && (
              <img
                src={getMediaUrl(item)}
                alt={item.description || "Item image"}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                onLoad={() =>
                  setLoadingImages(
                    (prev) => new Set([...prev].filter((x) => x !== item.id))
                  )
                }
                onError={() =>
                  setLoadingImages(
                    (prev) => new Set([...prev].filter((x) => x !== item.id))
                  )
                }
              />
            )}

            {item.type !== "image" && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  color: "text.secondary",
                }}
              >
                {getMediaIcon(item.type)}
                <Typography variant="caption">
                  {item.filename || item.type}
                </Typography>
              </Box>
            )}

            <Box
              sx={{
                position: "absolute",
                top: 4,
                right: 4,
                backgroundColor: "rgba(0,0,0,0.7)",
                color: "white",
                borderRadius: "50%",
                width: 24,
                height: 24,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.7rem",
              }}
            >
              <PreviewIcon fontSize="small" />
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              width: "100%",
              height: 80,
              mb: 0.5,
              borderRadius: 1,
              backgroundColor: "grey.50",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px dashed",
              borderColor: "grey.300",
            }}
          >
            <TextIcon sx={{ fontSize: 32, color: "grey.400" }} />
          </Box>
        )}

        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontSize: "0.65rem",
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={item.description || item.filename || "Item"}
          >
            {item.description || item.filename || "Item"}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: "0.6rem" }}
          >
            {getMediaEmoji(item.type)} {item.type?.toUpperCase()}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}
